import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../PurchaseOrderDetailsPage/PurchaseOrderDetailsPage.css";
import {
  getCartById,
  updateCartStatus,
} from "../../services/cartService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CART_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "request", label: "Request" },
  { value: "successful", label: "Successful" },
  { value: "failed", label: "Failed" },
  { value: "payment_confirmed", label: "Payment Confirmed" },
  { value: "payment_cancelled", label: "Payment Cancelled" },
  { value: "refund_processing", label: "Refund Processing" },
  { value: "refund_confirmed", label: "Refund Confirmed" },
  { value: "shopping_ongoing", label: "Shopping Ongoing" },
  { value: "product_checked_in", label: "Product Checked In" },
  { value: "arrived_at_hub", label: "Arrived At Hub" },
  { value: "delivered", label: "Delivered" },
  { value: "handled_by_user", label: "Handled By User" },
];

const formatCartStatus = (status: string): string => {
  const match = CART_STATUS_OPTIONS.find(
    (o) => o.value.toLowerCase() === (status || "").toLowerCase()
  );
  return match ? match.label : (status || "N/A").replace(/_/g, " ");
};

const getStatusColor = (status: string): string => {
  switch ((status || "").toLowerCase()) {
    case "request":
    case "product_checked_in":
      return "bg-blue-100 text-blue-800";
    case "successful":
    case "payment_confirmed":
    case "refund_confirmed":
    case "delivered":
      return "bg-green-100 text-green-800";
    case "failed":
    case "payment_cancelled":
      return "bg-red-100 text-red-800";
    case "refund_processing":
      return "bg-yellow-100 text-yellow-800";
    case "shopping_ongoing":
      return "bg-purple-100 text-purple-800";
    case "arrived_at_hub":
      return "bg-indigo-100 text-indigo-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`status-badge ${getStatusColor(status)}`}>
    {formatCartStatus(status)}
  </span>
);

const currencySymbol = (currency?: string) => {
  switch (currency) {
    case "GBP":
      return "£";
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "NGN":
      return "₦";
    default:
      return currency ? `${currency} ` : "";
  }
};

const AmazonOrderDetailsPage: React.FC = () => {
  const { cartId } = useParams<{ cartId: string }>();
  const navigate = useNavigate();

  const [cart, setCart] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const loadCart = async (id: string) => {
    const data = await getCartById(id);
    setCart(data);
    return data;
  };

  useEffect(() => {
    if (!cartId) {
      setError("No Cart ID provided.");
      setLoading(false);
      return;
    }

    const fetchCart = async () => {
      setLoading(true);
      setError(null);
      try {
        await loadCart(cartId);
      } catch {
        setError("Failed to fetch order details.");
      } finally {
        setLoading(false);
      }
    };

    fetchCart();
  }, [cartId]);

  const handleStatusChange = async (newStatus: string) => {
    if (!cart?.id) return;
    setIsUpdatingStatus(true);
    try {
      await updateCartStatus(cart.id, newStatus);
      setCart((prev: any) => ({ ...prev, status: newStatus }));
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="po-details-not-found">
        <h2>Loading Order...</h2>
      </div>
    );
  }
  if (error) {
    return (
      <div className="po-details-not-found">
        <h2>{error}</h2>
      </div>
    );
  }
  if (!cart) {
    return (
      <div className="po-details-not-found">
        <h2>Order Not Found</h2>
      </div>
    );
  }

  const customerName =
    `${cart.user?.firstName || ""} ${cart.user?.lastName || ""}`.trim() ||
    "N/A";
  const status = cart.status || "request";
  const currency = cart.currency || cart.transaction?.currency;

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="po-details-page">
        <div className="po-details-header">
          <div className="customer-info">
            <h2 className="ttiimm">
              <span
                onClick={() => navigate("/amazon")}
                className="back-link"
                style={{ cursor: "pointer" }}
              >
                Amazon
              </span>{" "}
              / Order Details
            </h2>
            <h1>
              {customerName}
              <StatusBadge status={status} />
            </h1>
            <p style={{ margin: "8px 0 0", color: "#666", fontSize: "14px" }}>
              {cart.user?.email}
            </p>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              alignItems: "flex-end",
            }}
          >
            <label style={{ fontSize: "12px", color: "#666" }}>
              Order Status
            </label>
            <select
              value={status}
              disabled={isUpdatingStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                minWidth: "200px",
              }}
            >
              {CART_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "24px",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          {cart.totalAmount !== undefined && (
            <div>
              <span style={{ fontSize: "12px", color: "#888" }}>
                Total Amount
              </span>
              <p style={{ margin: "4px 0 0", fontWeight: 600 }}>
                {currencySymbol(currency)}
                {Number(cart.totalAmount || 0).toLocaleString()}
              </p>
            </div>
          )}
          {cart.transaction?.paymentStatus && (
            <div>
              <span style={{ fontSize: "12px", color: "#888" }}>
                Payment Status
              </span>
              <p style={{ margin: "4px 0 0", fontWeight: 600 }}>
                {cart.transaction.paymentStatus}
              </p>
            </div>
          )}
          <div>
            <span style={{ fontSize: "12px", color: "#888" }}>Date</span>
            <p style={{ margin: "4px 0 0", fontWeight: 600 }}>
              {cart.createdAt
                ? new Date(cart.createdAt).toLocaleString()
                : "—"}
            </p>
          </div>
        </div>

        <div className="cart-con">Cart Items</div>

        <div className="cart-items-list">
          {(cart.items || []).map((item: any, index: number) => {
            const title =
              item.name || item.title || item.productName || item.asin || "Item";
            const link = item.link || item.url || item.productUrl;
            return (
              <div
                key={item.id || index}
                className="cart-item-card"
                style={{
                  backgroundColor: index % 2 === 0 ? "#FFF5EE" : "#f9f9f9",
                }}
              >
                <div className="cart-item1">
                  <div className="item-header">
                    <div className="header-row">
                      <div className="header-item">
                        <span className="label">ASIN</span>
                        <span className="value">{item.asin || "—"}</span>
                      </div>
                      <div className="header-item">
                        <span className="label">Quantity</span>
                        <span className="value">{item.quantity ?? "—"}</span>
                      </div>
                      {link && (
                        <div className="header-item">
                          <span className="label">Product Link</span>
                          <a
                            href={link}
                            className="link"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View on Amazon
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="item-content1">
                    <div className="content-section">
                      <div className="items-section">
                        <span className="section-label">Item</span>
                        <p className="items-text">{title}</p>
                      </div>
                      {(item.price !== undefined || item.amount !== undefined) && (
                        <div className="cost-section">
                          <span className="section-label">Price</span>
                          <div className="cost-row">
                            <span className="cost-amount">
                              {currencySymbol(item.currency || currency)}
                              {Number(
                                item.price ?? item.amount ?? 0
                              ).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    {item.details && (
                      <div className="details-section">
                        <span className="section-label">Details</span>
                        <p className="details-text">{item.details}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {(!cart.items || cart.items.length === 0) && (
            <div style={{ padding: "20px", color: "#666" }}>
              No items in this cart.
            </div>
          )}
        </div>

        {cart.transaction && (
          <div
            className="payment-summary-card"
            style={{
              marginTop: "24px",
              padding: "16px",
              background: "#f8f9fa",
              borderRadius: "8px",
            }}
          >
            <h4 style={{ margin: "0 0 12px" }}>Transaction</h4>
            <p style={{ margin: "4px 0", fontSize: "14px" }}>
              Reference: {cart.transaction.transactionReference || "—"}
            </p>
            <p style={{ margin: "4px 0", fontSize: "14px" }}>
              Amount:{" "}
              {currencySymbol(cart.transaction.currency || currency)}
              {Number(cart.transaction.amount || 0).toLocaleString()}
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default AmazonOrderDetailsPage;
